<script lang="ts">
  import { onMount } from 'svelte';
  import type { ApiClient } from './api';
  import { formatDate } from './format';
  import type { GuardDutyAcknowledgementEvent, GuardDutyFinding, StaffActor } from './types';

  let { api, finding, canManage, onChanged }: { api: ApiClient; finding: GuardDutyFinding; canManage: boolean; onChanged: () => Promise<void> } = $props();
  let events = $state<GuardDutyAcknowledgementEvent[]>([]);
  let cursor = $state<string | null>(null);
  let loading = $state(true);
  let changing = $state(false);
  let error = $state('');
  let message = $state('');

  function actorName(actor?: StaffActor): string {
    return actor?.username ?? actor?.subject ?? 'Alert Receiver';
  }

  function eventLabel(event: GuardDutyAcknowledgementEvent): string {
    if (event.action === 'acknowledged') return `${actorName(event.actor)} acknowledged this revision.`;
    if (event.action === 'restored') return `${actorName(event.actor)} restored the finding to active views.`;
    return 'A newer GuardDuty update returned the finding to active views.';
  }

  async function load(next = '') {
    loading = true;
    try {
      const result = await api.guardDutyAcknowledgementEvents(finding.id, next);
      events = next ? [...events, ...result.items] : result.items;
      cursor = result.nextCursor;
      error = '';
    } catch (failure) {
      error = failure instanceof Error ? failure.message : 'Acknowledgement activity could not be loaded.';
    } finally {
      loading = false;
    }
  }

  async function change(acknowledge: boolean) {
    changing = true;
    message = '';
    try {
      if (acknowledge) await api.acknowledgeGuardDutyFinding(finding.id);
      else await api.restoreGuardDutyFinding(finding.id);
      await Promise.all([onChanged(), load()]);
      message = acknowledge ? 'Finding acknowledged and hidden from active views.' : 'Finding restored to active views.';
    } catch (failure) {
      error = failure instanceof Error ? failure.message : 'The acknowledgement could not be changed.';
    } finally {
      changing = false;
    }
  }

  onMount(() => load());
</script>

<section class="detail-section" aria-labelledby="acknowledgement-heading">
  <div class="section-heading">
    <div>
      <h2 id="acknowledgement-heading">Acknowledgement</h2>
      <p class="muted">Acknowledgement is shared by the team and applies to this finding revision. A newer GuardDuty update returns it to active views.</p>
    </div>
    {#if finding.acknowledged}<span>Acknowledged</span>{/if}
  </div>
  {#if finding.acknowledged}
    <p class="current-state">Acknowledged {finding.acknowledgedAt ? formatDate(finding.acknowledgedAt) : ''} by {actorName(finding.acknowledgedBy)}.</p>
    {#if canManage}<button class="secondary" disabled={changing} onclick={() => change(false)}>{changing ? 'Restoring…' : 'Restore to active'}</button>{/if}
  {:else}
    <p class="current-state">This finding is visible in active views.</p>
    {#if canManage}<button class="primary" disabled={changing} onclick={() => change(true)}>{changing ? 'Acknowledging…' : 'Acknowledge finding'}</button>{/if}
  {/if}
  {#if message}<p class="form-message" aria-live="polite">{message}</p>{/if}
  {#if error}<p class="inline-error" role="alert">{error}</p>{/if}

  <div class="activity-heading"><h3>Acknowledgement activity</h3></div>
  {#if loading && events.length === 0}<p class="muted">Loading acknowledgement activity…</p>
  {:else if events.length === 0}<p class="muted">No acknowledgement actions have been recorded.</p>
  {:else}
    <ol class="activity-list">
      {#each events as event}
        <li><strong>{eventLabel(event)}</strong><span>{formatDate(event.occurredAt)} · Finding revision {formatDate(event.findingUpdatedAt)}</span></li>
      {/each}
    </ol>
    {#if cursor}<button class="secondary load-more" disabled={loading} onclick={() => load(cursor ?? '')}>{loading ? 'Loading…' : 'Load more activity'}</button>{/if}
  {/if}
</section>

<style>
  .section-heading { align-items: start; gap: 1rem; }
  .section-heading h2 { margin-bottom: .4rem; }
  .section-heading p { max-width: 46rem; margin-bottom: 1rem; line-height: 1.5; }
  .current-state { margin: .4rem 0 1rem; }
  .activity-heading { margin-top: 2rem; padding-top: 1.5rem; border-top: 1px solid var(--line); }
  .activity-heading h3 { margin-bottom: 1rem; }
  .activity-list { display: grid; gap: .75rem; margin: 0; padding: 0; list-style: none; }
  .activity-list li { display: grid; gap: .3rem; padding: .8rem 0; border-bottom: 1px solid var(--line); }
  .activity-list span { color: var(--muted); font-size: .8rem; }
  .inline-error { margin-top: 1rem; }
</style>
